import React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Box, Typography } from '@mui/material'
import { IconChevronRight } from '@tabler/icons-react'
import { ROUTES } from '../../../../helpers/routes-helper'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { useDevice } from '../../../../hooks/useDevice'
import {
  logEvent,
  flushEventsBeforeNavigation,
} from '../../../../analytics/analytics'
import { setPendingPointsClaimIntent } from '../../../../helpers/pointsClaimIntent'

const RewardsCTABanner: React.FC = () => {
  const { isHybrid } = useDevice()
  const { navigate, theme } = useAppUtilities()
  const { isAuthenticated, loginWithRedirect } = useAuth0()

  // Only render on hybrid/mobile
  if (!isHybrid) {
    return null
  }

  const isLoggedIn = isAuthenticated

  const handleBannerClick = (): void => {
    // Fire analytics event
    logEvent('rewards_cta_tapped', {
      isLoggedIn,
      destination: 'POINTS',
    })

    // Navigate/authenticate based on auth state
    if (isLoggedIn) {
      navigate(ROUTES.POINTS)
    } else {
      // Stash the points-claim intent BEFORE redirecting to signup (mirrors RewardsInfoPage's
      // "Claim my points" CTA). Without it BaseLayout's post-auth resume never fires, so the new
      // account is provisioned as a plain SIGNUP (no X-Points-Claim-Flow header) and the user is
      // dumped into onboarding instead of landing back on the points page.
      setPendingPointsClaimIntent({ returnRoute: ROUTES.POINTS })
      // Signup funnel entry (rewards banner redirects to the Auth0 signup screen).
      logEvent('signup_started', {
        screen_name: 'home',
        source: 'rewards',
        mode: 'signup',
      })
      // Hand the queue to a keepalive/beacon flush BEFORE the Auth0 redirect tears down the
      // document — otherwise the in-flight POST /events is cancelled and signup_started is lost
      // (which is why source:'rewards' never reached BigQuery). Mirrors useAuthHelper.
      flushEventsBeforeNavigation()
      // Trigger Auth0 login with returnTo set to POINTS page
      loginWithRedirect({
        authorizationParams: { screen_hint: 'signup' },
        appState: { returnTo: ROUTES.POINTS },
      }).catch(() => {
        // Fallback to navigate if loginWithRedirect fails
        navigate(ROUTES.POINTS)
      })
    }
  }

  return (
    <Box
      onClick={handleBannerClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px',
        paddingLeft: '12px', // Reduced for accent bar
        marginTop: '16px',
        marginBottom: '16px',
        marginX: 0,
        borderRadius: '12px',
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        '&:active': {
          backgroundColor: theme.palette.grey[50],
          borderColor: theme.palette.grey[300],
        },
        '&:hover': {
          borderColor: theme.palette.grey[300],
          filter: 'drop-shadow(0px 2px 4px 0px #6666664D)',
        },
      }}
    >
      {/* Left: Title + Subtext */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, cursor: 'pointer' }}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            color: theme.palette.grey[900],
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {isLoggedIn ? 'Your rewards' : 'Earn as you dine'}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            marginTop: '4px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {isLoggedIn ? "See what you've earned" : 'Sign up to keep your points'}
        </Typography>
      </Box>

      {/* Right: Chevron Icon */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: '12px',
          flexShrink: 0,
        }}
      >
        <IconChevronRight
          size={20}
          stroke={2}
          color={theme.palette.grey[600]}
        />
      </Box>
    </Box>
  )
}

export default RewardsCTABanner
